import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MapPin, Ticket, Users, ExternalLink, Pencil, Trash2, Check, CalendarPlus, Download } from "lucide-react";
import { toast } from "sonner";
import { fetchEvent, fetchMyRsvp, rsvpEvent, cancelRsvp, deleteHostEvent, exportEventAttendeesCsv, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadIcs } from "@/lib/ics";

const fmtFull = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  } catch { return iso; }
};

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [e, setE] = useState(null);
  const [loading, setLoading] = useState(true);
  const [going, setGoing] = useState(false);
  const [rsvpCount, setRsvpCount] = useState(0);
  const [rsvpBusy, setRsvpBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchEvent(id)
      .then((data) => { setE(data); setRsvpCount(data.rsvp_count || 0); })
      .catch(() => setE(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (user && e) {
      fetchMyRsvp(id).then((r) => { setGoing(r.going); setRsvpCount(r.rsvp_count); }).catch(() => {});
    }
  }, [user, e, id]);

  const isOwner = user && e && (e.owner_user_id === user.user_id || user.role === "admin");
  const isCommunity = e?.source === "community";

  const toggleRsvp = async () => {
    if (!user) { navigate("/login"); return; }
    setRsvpBusy(true);
    try {
      const r = going ? await cancelRsvp(id) : await rsvpEvent(id);
      setGoing(r.going);
      setRsvpCount(r.rsvp_count);
      toast.success(r.going ? "You're on the list!" : "RSVP cancelled");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update RSVP");
    } finally {
      setRsvpBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this event permanently?")) return;
    try {
      await deleteHostEvent(id);
      toast.success("Event deleted");
      navigate("/dashboard");
    } catch {
      toast.error("Could not delete event");
    }
  };

  const onExportAttendees = async () => {
    try {
      const blob = await exportEventAttendeesCsv(id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(e.name || "event").replace(/[^a-z0-9]+/gi, "_")}_attendees.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-32 font-serif italic text-xl text-sand-700">Loading…</div>;
  if (!e) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center">
      <p className="font-serif italic text-2xl text-sand-700">Event not found.</p>
      <Link to="/events" className="mt-6 inline-block font-mono text-xs tracking-widest uppercase text-brand-500">← All events</Link>
    </div>
  );

  return (
    <div data-testid="event-detail-page" className="bg-sand-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-10 flex items-center justify-between gap-4">
        <Link to="/events" data-testid="back-to-events" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-sand-700 hover:text-brand-500 transition-colors">
          <ArrowLeft size={13} /> Back to Calendar
        </Link>
        {isOwner && isCommunity && (
          <div className="flex items-center gap-2">
            <button onClick={onExportAttendees} data-testid="export-attendees-btn" className="inline-flex items-center gap-1.5 border border-sand-300 px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-sand-900 hover:border-brand-500">
              <Download size={12} /> Attendees ({rsvpCount})
            </button>
            <Link to={`/events/${id}/edit`} data-testid="edit-event-btn" className="inline-flex items-center gap-1.5 border border-sand-300 px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-sand-900 hover:border-brand-500">
              <Pencil size={12} /> Edit
            </Link>
            <button onClick={onDelete} data-testid="delete-event-btn" className="inline-flex items-center gap-1.5 border border-sand-300 px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-sand-900 hover:border-brand-500 hover:text-brand-500">
              <Trash2 size={12} /> Delete
            </button>
          </div>
        )}
      </div>

      {isOwner && e.status !== "published" && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-6">
          <div data-testid="event-status-banner" className="bg-ochre-400/20 border border-ochre-400 text-sand-900 px-5 py-3 font-mono text-[11px] tracking-widest uppercase">
            Status: {e.status} — {e.status === "pending" ? "awaiting editor review before it goes public." : "this event is not public."}
          </div>
        </div>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="img-zoom aspect-[21/9] mb-12 relative bg-sand-200">
          {e.cover_image && <img src={e.cover_image} alt={e.name} className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 inset-x-0 p-8 md:p-14">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-ochre-400 mb-4 flex items-center gap-3">
              {e.category}
              {isCommunity && <span className="bg-forest-500 text-sand-50 px-2 py-0.5">Community</span>}
            </div>
            <h1 className="font-serif font-black text-4xl sm:text-5xl lg:text-7xl leading-[1.02] text-sand-50 max-w-4xl">
              {e.name}
            </h1>
            {e.host_name && (
              <p className="mt-3 font-mono text-[11px] tracking-widest uppercase text-sand-50/80">
                Hosted by {e.host_name}{e.host_type ? ` · ${e.host_type}` : ""}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <p className="font-serif text-xl md:text-2xl leading-relaxed text-sand-900 drop-cap">
              {e.long_description || e.short_description}
            </p>

            {e.sponsors?.length > 0 && (
              <div className="mt-16">
                <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-5">Presented With</div>
                <div className="flex flex-wrap gap-3">
                  {e.sponsors.map((s) => (
                    <span key={s} className="px-5 py-3 border border-sand-300 font-mono text-xs tracking-widest uppercase text-sand-900">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="bg-white border border-sand-200 p-8 sticky top-28">
              <div className="space-y-6">
                <div className="border-l-2 border-brand-500 pl-4">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-sand-600 mb-1 flex items-center gap-2"><Calendar size={11} /> When</div>
                  <p className="font-serif text-lg text-sand-900">{fmtFull(e.date)}</p>
                  {e.end_date && <p className="font-serif text-sm text-sand-700 italic">through {fmtFull(e.end_date)}</p>}
                  {(e.start_time || e.end_time) && (
                    <p className="mt-1 font-mono text-[11px] tracking-widest uppercase text-sand-700 flex items-center gap-1.5">
                      <Clock size={11} /> {e.start_time}{e.end_time ? ` – ${e.end_time}` : ""}
                    </p>
                  )}
                </div>
                <div className="border-l-2 border-brand-500 pl-4">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-sand-600 mb-1 flex items-center gap-2"><MapPin size={11} /> Where</div>
                  <p className="font-serif text-lg text-sand-900">{e.venue || e.location || e.city}</p>
                  {e.venue && e.location && <p className="font-serif text-sm text-sand-700">{e.location}</p>}
                </div>
                <div className="border-l-2 border-brand-500 pl-4">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-sand-600 mb-1 flex items-center gap-2"><Ticket size={11} /> Tickets</div>
                  <p className="font-serif text-lg text-sand-900">{e.ticket_price}</p>
                </div>
                <div className="border-l-2 border-brand-500 pl-4">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-sand-600 mb-1 flex items-center gap-2"><Users size={11} /> Going</div>
                  <p className="font-serif text-lg text-sand-900" data-testid="event-rsvp-count">
                    {rsvpCount}{e.capacity ? ` / ${e.capacity}` : ""}
                  </p>
                </div>
              </div>

              {e.ticket_type === "external" && e.ticket_url ? (
                <a
                  href={e.ticket_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="event-ticket-link"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-sand-900 text-sand-50 px-6 py-4 font-mono text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-brand-500 transition-colors active:scale-95"
                >
                  Get Tickets <ExternalLink size={13} />
                </a>
              ) : (
                <button
                  onClick={toggleRsvp}
                  disabled={rsvpBusy}
                  data-testid="event-rsvp-btn"
                  className={`mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 font-mono text-[11px] font-bold tracking-[0.25em] uppercase transition-colors active:scale-95 disabled:opacity-60 ${
                    going ? "bg-forest-500 text-sand-50 hover:bg-forest-600" : "bg-sand-900 text-sand-50 hover:bg-brand-500"
                  }`}
                >
                  {going ? <><Check size={14} /> You're Going</> : "RSVP — I'm Going"}
                </button>
              )}
              {!user && (
                <p className="mt-3 text-center font-mono text-[10px] tracking-widest uppercase text-sand-500">
                  <Link to="/login" className="editorial-link text-brand-500">Sign in</Link> to RSVP
                </p>
              )}
              <button
                onClick={() => downloadIcs(e)}
                data-testid="add-to-calendar-btn"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-sand-300 text-sand-900 px-6 py-3 font-mono text-[10px] font-bold tracking-[0.25em] uppercase hover:border-brand-500 hover:text-brand-500 transition-colors"
              >
                <CalendarPlus size={13} /> Add to Calendar
              </button>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
